import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed } from 'vue'
import type { ComputedRef } from 'vue'

import { fetchStopFleetRefresh, sortByArrival } from '@/services/timbusApi'
import { WorkerUnauthorizedError } from '@/services/workerApi'
import type { BusRoute, MapVehicle } from '@/types'
import { DISCOVERY_SLICE_COUNT, vehiclesAtStop } from '@/utils/vehiclePolling'

import { LIVE_VEHICLE_REFETCH_INTERVAL, LIVE_VEHICLE_STALE_TIME } from './liveVehicleQueryPolicy'
import { useWorkerAuth } from './useWorkerAuth'

/**
 * Live buses approaching a stop, across every route serving it.
 *
 * Mirrors `useRouteVehiclesQuery`'s cadence deliberately — same 5 s interval,
 * same `retry: false`, same staleTime just under the interval — so the two
 * screens refresh at the same rhythm and neither doubles up requests against a
 * third-party service.
 */
export function useStopVehiclesQuery(
  stopId: ComputedRef<number | undefined>,
  routeCodes: ComputedRef<string[]>,
  routes: ComputedRef<BusRoute[]> = computed(() => []),
) {
  const queryClient = useQueryClient()
  const { session, clearSession } = useWorkerAuth()
  let hydrationSlice = 0
  const hydratedRoutes = new Map<number, Map<string, MapVehicle>>()
  return useQuery({
    // Codes are part of the key: navigating between stops must not serve the
    // previous stop's buses while the new request is in flight.
    queryKey: computed(() => ['stop-vehicles', stopId.value, routeCodes.value] as const),
    queryFn: async ({ signal }) => {
      const id = stopId.value
      if (id === undefined || routeCodes.value.length === 0 || !session.value) return []
      try {
        // A route screen may already own a complete fleet snapshot for one or
        // more passing routes. Reuse those app-level query results to augment
        // the exact selected-stop rows returned by the combined request below.
        // Empty arrays count as a cached answer; undefined means that route has
        // never been loaded in this session.
        const cached: MapVehicle[] = []
        const routesToHydrate: BusRoute[] = []
        const seenRoutes = new Set<number>()
        for (const route of routes.value) {
          if (seenRoutes.has(route.id)) continue
          seenRoutes.add(route.id)
          const snapshot = queryClient.getQueryData<MapVehicle[]>(['route-vehicles', route.id])
          if (snapshot !== undefined) cached.push(...vehiclesAtStop(route, id, snapshot))
          else {
            const hydrated = hydratedRoutes.get(route.id)
            if (hydrated) cached.push(...vehiclesAtStop(route, id, [...hydrated.values()]))
            routesToHydrate.push(route)
          }
        }

        // Keep this one combined station request on every five-second tick,
        // even when route snapshots are available. It refreshes exact selected
        // stop telemetry and gives the cache merge a live source without
        // creating one request per route or one poller per passing route.
        const refresh = await fetchStopFleetRefresh(id, routeCodes.value, {
          signal,
          session: session.value,
          routeSlice: {
            routes: routesToHydrate,
            slice: hydrationSlice % DISCOVERY_SLICE_COUNT,
          },
        })
        hydrationSlice += 1
        for (const [routeId, observations] of refresh.routeVehicles) {
          const hydrated = hydratedRoutes.get(routeId) ?? new Map<string, MapVehicle>()
          for (const vehicle of observations) hydrated.set(vehicle.id, vehicle)
          hydratedRoutes.set(routeId, hydrated)
        }
        for (const route of routesToHydrate) {
          const hydrated = hydratedRoutes.get(route.id)
          if (hydrated) cached.push(...vehiclesAtStop(route, id, [...hydrated.values()]))
        }

        // Prefer the exact stop observation over the route snapshot for the
        // same bus, while retaining every other cached fleet row. The parser's
        // collision-safe id is the row identity: using plate alone here would
        // collapse two valid records that temporarily share a plate/direction.
        const unique = new Map<string, MapVehicle>()
        for (const vehicle of cached) unique.set(vehicle.id, vehicle)
        for (const vehicle of refresh.stopVehicles) unique.set(vehicle.id, vehicle)
        return sortByArrival([...unique.values()])
      } catch (error) {
        if (error instanceof WorkerUnauthorizedError) clearSession()
        throw error
      }
    },
    enabled: computed(
      () => stopId.value !== undefined && routeCodes.value.length > 0 && session.value !== null,
    ),
    refetchInterval: LIVE_VEHICLE_REFETCH_INTERVAL,
    refetchIntervalInBackground: false,
    retry: false,
    staleTime: LIVE_VEHICLE_STALE_TIME,
  })
}
